/**
 * Parish income tax rates (kirkollisveroprosentti), 2026, per municipality: the Evangelical
 * Lutheran parish and the Orthodox parish serving that municipality.
 *
 * Source: Verohallinto, decision "Kuntien ja seurakuntien tuloveroprosentit vuonna 2026"
 * (https://www.vero.fi/syventavat-vero-ohjeet/paatokset/47465/kuntien-ja-seurakuntien-tuloveroprosentit-vuonna-2026/,
 * the table on the page, accessed 2026-09-06; also published in Suomen saadoskokoelma 1012/2025).
 * The municipal column of that same table matches finnish-municipal-tax-rates-2026.ts for all
 * 308 municipalities. Parsed by script from the page's data table, not typed by hand.
 *
 * Members of the Swedish-speaking parishes pay their own parish's rate (Finnish-Swedish
 * Evangelical Lutheran parish 1.10%, Olaus Petri 1.00%); this table lists the local
 * Finnish-speaking Evangelical Lutheran parish, which is what the calculator uses.
 */

export interface ParishTaxRates {
  /** Evangelical Lutheran parish, % of taxable income. */
  evLut: number;
  /** Orthodox parish, % of taxable income. */
  orthodox: number;
}

/** Income-weighted national average Evangelical Lutheran rate, as used by Veronmaksajat's 2026
 *  wage-earner tables; the calculator's church tax when no municipality is chosen. */
export const PARISH_TAX_AVG_RATE_PCT = 1.38;

export const FINNISH_PARISH_TAX_RATES_2026: Record<string, ParishTaxRates> = {
  "Akaa": { evLut: 1.7, orthodox: 2.0 },
  "Alajärvi": { evLut: 1.7, orthodox: 2.0 },
  "Alavieska": { evLut: 1.8, orthodox: 2.25 },
  "Alavus": { evLut: 1.75, orthodox: 2.0 },
  "Asikkala": { evLut: 1.75, orthodox: 1.8 },
  "Askola": { evLut: 1.75, orthodox: 1.8 },
  "Aura": { evLut: 1.3, orthodox: 1.75 },
  "Brändö": { evLut: 2.0, orthodox: 1.75 },
  "Eckerö": { evLut: 1.8, orthodox: 1.75 },
  "Enonkoski": { evLut: 1.6, orthodox: 1.95 },
  "Enontekiö": { evLut: 1.7, orthodox: 2.25 },
  "Espoo": { evLut: 1.0, orthodox: 1.8 },
  "Eura": { evLut: 1.45, orthodox: 1.75 },
  "Eurajoki": { evLut: 1.65, orthodox: 2.0 },
  "Evijärvi": { evLut: 1.7, orthodox: 2.0 },
  "Finström": { evLut: 1.95, orthodox: 1.75 },
  "Forssa": { evLut: 1.4, orthodox: 1.8 },
  "Föglö": { evLut: 2.0, orthodox: 1.75 },
  "Geta": { evLut: 1.95, orthodox: 1.75 },
  "Haapajärvi": { evLut: 1.7, orthodox: 2.0 },
  "Haapavesi": { evLut: 1.8, orthodox: 2.0 },
  "Hailuoto": { evLut: 1.8, orthodox: 2.25 },
  "Halsua": { evLut: 1.65, orthodox: 2.0 },
  "Hamina": { evLut: 1.55, orthodox: 1.95 },
  "Hammarland": { evLut: 1.8, orthodox: 1.75 },
  "Hankasalmi": { evLut: 1.95, orthodox: 1.95 },
  "Hanko": { evLut: 1.6, orthodox: 1.8 },
  "Harjavalta": { evLut: 1.7, orthodox: 1.75 },
  "Hartola": { evLut: 1.7, orthodox: 1.95 },
  "Hattula": { evLut: 1.45, orthodox: 1.8 },
  "Hausjärvi": { evLut: 1.75, orthodox: 1.8 },
  "Heinola": { evLut: 1.45, orthodox: 1.8 },
  "Heinävesi": { evLut: 1.6, orthodox: 1.95 },
  "Helsinki": { evLut: 1.0, orthodox: 1.8 },
  "Hirvensalmi": { evLut: 1.7, orthodox: 1.95 },
  "Hollola": { evLut: 1.7, orthodox: 1.8 },
  "Huittinen": { evLut: 1.65, orthodox: 1.75 },
  "Humppila": { evLut: 1.8, orthodox: 1.8 },
  "Hyrynsalmi": { evLut: 1.75, orthodox: 2.25 },
  "Hyvinkää": { evLut: 1.25, orthodox: 1.8 },
  "Hämeenkyrö": { evLut: 1.7, orthodox: 2.0 },
  "Hämeenlinna": { evLut: 1.3, orthodox: 1.8 },
  "Ii": { evLut: 1.5, orthodox: 2.25 },
  "Iisalmi": { evLut: 1.65, orthodox: 2.0 },
  "Iitti": { evLut: 1.65, orthodox: 1.8 },
  "Ikaalinen": { evLut: 1.85, orthodox: 2.0 },
  "Ilmajoki": { evLut: 1.7, orthodox: 2.0 },
  "Ilomantsi": { evLut: 1.6, orthodox: 2.0 },
  "Imatra": { evLut: 1.65, orthodox: 1.95 },
  "Inari": { evLut: 1.7, orthodox: 2.25 },
  "Inkoo": { evLut: 1.75, orthodox: 1.8 },
  "Isojoki": { evLut: 1.75, orthodox: 2.0 },
  "Isokyrö": { evLut: 1.75, orthodox: 2.0 },
  "Janakkala": { evLut: 1.65, orthodox: 1.8 },
  "Joensuu": { evLut: 1.5, orthodox: 2.0 },
  "Jokioinen": { evLut: 1.8, orthodox: 1.8 },
  "Jomala": { evLut: 1.7, orthodox: 1.75 },
  "Joroinen": { evLut: 1.7, orthodox: 1.95 },
  "Joutsa": { evLut: 1.45, orthodox: 1.95 },
  "Juuka": { evLut: 1.7, orthodox: 1.85 },
  "Juupajoki": { evLut: 1.75, orthodox: 2.0 },
  "Juva": { evLut: 1.65, orthodox: 1.95 },
  "Jyväskylä": { evLut: 1.45, orthodox: 1.95 },
  "Jämijärvi": { evLut: 1.8, orthodox: 2.0 },
  "Jämsä": { evLut: 1.55, orthodox: 2.0 },
  "Järvenpää": { evLut: 1.25, orthodox: 1.8 },
  "Kaarina": { evLut: 1.2, orthodox: 1.75 },
  "Kaavi": { evLut: 1.45, orthodox: 2.0 },
  "Kajaani": { evLut: 1.6, orthodox: 2.25 },
  "Kalajoki": { evLut: 1.6, orthodox: 2.25 },
  "Kangasala": { evLut: 1.35, orthodox: 2.0 },
  "Kangasniemi": { evLut: 1.65, orthodox: 1.95 },
  "Kankaanpää": { evLut: 1.8, orthodox: 2.0 },
  "Kannonkoski": { evLut: 1.75, orthodox: 1.95 },
  "Kannus": { evLut: 1.7, orthodox: 2.0 },
  "Karijoki": { evLut: 1.8, orthodox: 2.0 },
  "Karkkila": { evLut: 1.5, orthodox: 1.8 },
  "Karstula": { evLut: 1.75, orthodox: 1.95 },
  "Karvia": { evLut: 1.8, orthodox: 2.0 },
  "Kaskinen": { evLut: 1.85, orthodox: 2.0 },
  "Kauhajoki": { evLut: 1.8, orthodox: 2.0 },
  "Kauhava": { evLut: 1.7, orthodox: 2.0 },
  "Kauniainen": { evLut: 1.0, orthodox: 1.8 },
  "Kaustinen": { evLut: 1.65, orthodox: 2.0 },
  "Keitele": { evLut: 1.65, orthodox: 2.0 },
  "Kemi": { evLut: 1.6, orthodox: 2.25 },
  "Kemijärvi": { evLut: 1.7, orthodox: 2.25 },
  "Keminmaa": { evLut: 1.35, orthodox: 2.25 },
  "Kemiönsaari": { evLut: 2.0, orthodox: 1.75 },
  "Kempele": { evLut: 1.45, orthodox: 2.25 },
  "Kerava": { evLut: 1.25, orthodox: 1.8 },
  "Keuruu": { evLut: 1.8, orthodox: 1.95 },
  "Kihniö": { evLut: 1.9, orthodox: 2.0 },
  "Kinnula": { evLut: 1.8, orthodox: 1.95 },
  "Kirkkonummi": { evLut: 1.25, orthodox: 1.8 },
  "Kitee": { evLut: 1.8, orthodox: 2.0 },
  "Kittilä": { evLut: 1.6, orthodox: 2.25 },
  "Kiuruvesi": { evLut: 1.5, orthodox: 2.0 },
  "Kivijärvi": { evLut: 1.75, orthodox: 1.95 },
  "Kokemäki": { evLut: 1.85, orthodox: 1.75 },
  "Kokkola": { evLut: 1.65, orthodox: 2.0 },
  "Kolari": { evLut: 1.8, orthodox: 2.25 },
  "Konnevesi": { evLut: 1.65, orthodox: 1.95 },
  "Kontiolahti": { evLut: 1.5, orthodox: 2.0 },
  "Korsnäs": { evLut: 1.85, orthodox: 2.0 },
  "Koski Tl": { evLut: 1.45, orthodox: 1.75 },
  "Kotka": { evLut: 1.5, orthodox: 1.95 },
  "Kouvola": { evLut: 1.55, orthodox: 1.95 },
  "Kristiinankaupunki": { evLut: 2.0, orthodox: 2.0 },
  "Kruunupyy": { evLut: 2.0, orthodox: 2.0 },
  "Kuhmo": { evLut: 1.75, orthodox: 2.25 },
  "Kuhmoinen": { evLut: 1.7, orthodox: 2.0 },
  "Kumlinge": { evLut: 2.0, orthodox: 1.75 },
  "Kuopio": { evLut: 1.45, orthodox: 2.0 },
  "Kuortane": { evLut: 1.85, orthodox: 2.0 },
  "Kurikka": { evLut: 1.7, orthodox: 2.0 },
  "Kustavi": { evLut: 1.8, orthodox: 1.75 },
  "Kuusamo": { evLut: 1.55, orthodox: 2.25 },
  "Kyyjärvi": { evLut: 1.75, orthodox: 1.95 },
  "Kärkölä": { evLut: 1.7, orthodox: 1.8 },
  "Kärsämäki": { evLut: 1.8, orthodox: 2.0 },
  "Kökar": { evLut: 2.0, orthodox: 1.75 },
  "Lahti": { evLut: 1.45, orthodox: 1.8 },
  "Laihia": { evLut: 1.5, orthodox: 2.0 },
  "Laitila": { evLut: 1.6, orthodox: 1.75 },
  "Lapinjärvi": { evLut: 1.65, orthodox: 1.95 },
  "Lapinlahti": { evLut: 1.65, orthodox: 2.0 },
  "Lappajärvi": { evLut: 1.7, orthodox: 2.0 },
  "Lappeenranta": { evLut: 1.4, orthodox: 1.95 },
  "Lapua": { evLut: 1.75, orthodox: 2.0 },
  "Laukaa": { evLut: 1.65, orthodox: 1.95 },
  "Lemi": { evLut: 1.75, orthodox: 1.95 },
  "Lemland": { evLut: 1.7, orthodox: 1.75 },
  "Lempäälä": { evLut: 1.45, orthodox: 2.0 },
  "Leppävirta": { evLut: 1.6, orthodox: 1.95 },
  "Lestijärvi": { evLut: 2.0, orthodox: 2.0 },
  "Lieksa": { evLut: 1.55, orthodox: 2.0 },
  "Lieto": { evLut: 1.3, orthodox: 1.75 },
  "Liminka": { evLut: 1.7, orthodox: 2.25 },
  "Liperi": { evLut: 1.6, orthodox: 1.85 },
  "Lohja": { evLut: 1.5, orthodox: 1.8 },
  "Loimaa": { evLut: 1.55, orthodox: 1.75 },
  "Loppi": { evLut: 1.75, orthodox: 1.8 },
  "Loviisa": { evLut: 1.65, orthodox: 1.95 },
  "Luhanka": { evLut: 1.45, orthodox: 1.95 },
  "Lumijoki": { evLut: 1.95, orthodox: 2.25 },
  "Lumparland": { evLut: 1.7, orthodox: 1.75 },
  "Luoto": { evLut: 1.9, orthodox: 2.0 },
  "Luumäki": { evLut: 1.7, orthodox: 1.95 },
  "Maalahti": { evLut: 1.9, orthodox: 2.0 },
  "Maarianhamina": { evLut: 1.55, orthodox: 1.75 },
  "Marttila": { evLut: 1.45, orthodox: 1.75 },
  "Masku": { evLut: 1.6, orthodox: 1.75 },
  "Merijärvi": { evLut: 1.75, orthodox: 2.25 },
  "Merikarvia": { evLut: 1.9, orthodox: 2.0 },
  "Miehikkälä": { evLut: 1.55, orthodox: 1.95 },
  "Mikkeli": { evLut: 1.45, orthodox: 1.95 },
  "Muhos": { evLut: 1.65, orthodox: 2.25 },
  "Multia": { evLut: 1.8, orthodox: 1.95 },
  "Muonio": { evLut: 1.7, orthodox: 2.25 },
  "Mustasaari": { evLut: 1.8, orthodox: 2.0 },
  "Muurame": { evLut: 1.55, orthodox: 1.95 },
  "Mynämäki": { evLut: 1.65, orthodox: 1.75 },
  "Myrskylä": { evLut: 1.85, orthodox: 1.95 },
  "Mäntsälä": { evLut: 1.25, orthodox: 1.8 },
  "Mänttä-Vilppula": { evLut: 1.8, orthodox: 2.0 },
  "Mäntyharju": { evLut: 1.9, orthodox: 1.95 },
  "Naantali": { evLut: 1.35, orthodox: 1.75 },
  "Nakkila": { evLut: 1.75, orthodox: 2.0 },
  "Nivala": { evLut: 1.75, orthodox: 2.0 },
  "Nokia": { evLut: 1.5, orthodox: 2.0 },
  "Nousiainen": { evLut: 1.55, orthodox: 1.75 },
  "Nurmes": { evLut: 1.7, orthodox: 1.85 },
  "Nurmijärvi": { evLut: 1.45, orthodox: 1.8 },
  "Närpiö": { evLut: 1.5, orthodox: 2.0 },
  "Orimattila": { evLut: 1.75, orthodox: 1.8 },
  "Oripää": { evLut: 1.75, orthodox: 1.75 },
  "Orivesi": { evLut: 1.75, orthodox: 2.0 },
  "Oulainen": { evLut: 1.75, orthodox: 2.25 },
  "Oulu": { evLut: 1.3, orthodox: 2.25 },
  "Outokumpu": { evLut: 1.6, orthodox: 1.85 },
  "Padasjoki": { evLut: 1.7, orthodox: 1.8 },
  "Paimio": { evLut: 1.45, orthodox: 1.75 },
  "Paltamo": { evLut: 1.65, orthodox: 2.25 },
  "Parainen": { evLut: 1.6, orthodox: 1.75 },
  "Parikkala": { evLut: 1.7, orthodox: 1.95 },
  "Parkano": { evLut: 1.7, orthodox: 2.0 },
  "Pedersören kunta": { evLut: 1.8, orthodox: 2.0 },
  "Pelkosenniemi": { evLut: 1.95, orthodox: 2.25 },
  "Pello": { evLut: 1.7, orthodox: 2.25 },
  "Perho": { evLut: 1.9, orthodox: 2.0 },
  "Petäjävesi": { evLut: 1.9, orthodox: 1.95 },
  "Pieksämäki": { evLut: 1.45, orthodox: 1.95 },
  "Pielavesi": { evLut: 1.65, orthodox: 2.0 },
  "Pietarsaari": { evLut: 1.8, orthodox: 2.0 },
  "Pihtipudas": { evLut: 1.8, orthodox: 1.95 },
  "Pirkkala": { evLut: 1.4, orthodox: 2.0 },
  "Polvijärvi": { evLut: 1.6, orthodox: 1.85 },
  "Pomarkku": { evLut: 1.5, orthodox: 2.0 },
  "Pori": { evLut: 1.5, orthodox: 2.0 },
  "Pornainen": { evLut: 1.75, orthodox: 1.8 },
  "Porvoo": { evLut: 1.4, orthodox: 1.8 },
  "Posio": { evLut: 1.75, orthodox: 2.25 },
  "Pudasjärvi": { evLut: 1.5, orthodox: 2.25 },
  "Pukkila": { evLut: 1.75, orthodox: 1.8 },
  "Punkalaidun": { evLut: 1.7, orthodox: 1.75 },
  "Puolanka": { evLut: 1.75, orthodox: 2.25 },
  "Puumala": { evLut: 1.45, orthodox: 1.95 },
  "Pyhtää": { evLut: 1.7, orthodox: 1.95 },
  "Pyhäjoki": { evLut: 1.75, orthodox: 2.25 },
  "Pyhäjärvi": { evLut: 1.75, orthodox: 2.0 },
  "Pyhäntä": { evLut: 1.9, orthodox: 2.0 },
  "Pyhäranta": { evLut: 1.9, orthodox: 1.75 },
  "Pälkäne": { evLut: 1.8, orthodox: 2.0 },
  "Pöytyä": { evLut: 1.75, orthodox: 1.75 },
  "Raahe": { evLut: 1.9, orthodox: 2.25 },
  "Raasepori": { evLut: 1.5, orthodox: 1.8 },
  "Raisio": { evLut: 1.15, orthodox: 1.75 },
  "Rantasalmi": { evLut: 1.6, orthodox: 1.95 },
  "Ranua": { evLut: 1.75, orthodox: 2.25 },
  "Rauma": { evLut: 1.5, orthodox: 1.75 },
  "Rautalampi": { evLut: 1.85, orthodox: 2.0 },
  "Rautavaara": { evLut: 1.8, orthodox: 1.85 },
  "Rautjärvi": { evLut: 1.75, orthodox: 1.95 },
  "Reisjärvi": { evLut: 1.7, orthodox: 2.0 },
  "Riihimäki": { evLut: 1.3, orthodox: 1.8 },
  "Ristijärvi": { evLut: 1.65, orthodox: 2.25 },
  "Rovaniemi": { evLut: 1.4, orthodox: 2.25 },
  "Ruokolahti": { evLut: 1.75, orthodox: 1.95 },
  "Ruovesi": { evLut: 1.75, orthodox: 2.0 },
  "Rusko": { evLut: 1.45, orthodox: 1.75 },
  "Rääkkylä": { evLut: 1.8, orthodox: 2.0 },
  "Saarijärvi": { evLut: 1.75, orthodox: 1.95 },
  "Salla": { evLut: 1.85, orthodox: 2.25 },
  "Salo": { evLut: 1.45, orthodox: 1.75 },
  "Saltvik": { evLut: 1.75, orthodox: 1.75 },
  "Sastamala": { evLut: 1.75, orthodox: 2.0 },
  "Sauvo": { evLut: 1.45, orthodox: 1.75 },
  "Savitaipale": { evLut: 1.75, orthodox: 1.95 },
  "Savonlinna": { evLut: 1.6, orthodox: 1.95 },
  "Savukoski": { evLut: 1.95, orthodox: 2.25 },
  "Seinäjoki": { evLut: 1.6, orthodox: 2.0 },
  "Sievi": { evLut: 1.85, orthodox: 2.0 },
  "Siikainen": { evLut: 1.8, orthodox: 2.0 },
  "Siikajoki": { evLut: 1.9, orthodox: 2.25 },
  "Siikalatva": { evLut: 1.9, orthodox: 2.25 },
  "Siilinjärvi": { evLut: 1.35, orthodox: 2.0 },
  "Simo": { evLut: 1.75, orthodox: 2.25 },
  "Sipoo": { evLut: 1.35, orthodox: 1.8 },
  "Siuntio": { evLut: 1.65, orthodox: 1.8 },
  "Sodankylä": { evLut: 1.4, orthodox: 2.25 },
  "Soini": { evLut: 1.85, orthodox: 2.0 },
  "Somero": { evLut: 1.65, orthodox: 1.8 },
  "Sonkajärvi": { evLut: 1.65, orthodox: 2.0 },
  "Sotkamo": { evLut: 1.6, orthodox: 2.25 },
  "Sottunga": { evLut: 2.0, orthodox: 1.75 },
  "Sulkava": { evLut: 1.6, orthodox: 1.95 },
  "Sund": { evLut: 1.95, orthodox: 1.75 },
  "Suomussalmi": { evLut: 1.7, orthodox: 2.25 },
  "Suonenjoki": { evLut: 1.75, orthodox: 2.0 },
  "Sysmä": { evLut: 1.7, orthodox: 1.95 },
  "Säkylä": { evLut: 1.8, orthodox: 1.75 },
  "Taipalsaari": { evLut: 1.75, orthodox: 1.95 },
  "Taivalkoski": { evLut: 1.65, orthodox: 2.25 },
  "Taivassalo": { evLut: 1.7, orthodox: 1.75 },
  "Tammela": { evLut: 1.7, orthodox: 1.8 },
  "Tampere": { evLut: 1.25, orthodox: 2.0 },
  "Tervo": { evLut: 1.85, orthodox: 2.0 },
  "Tervola": { evLut: 1.75, orthodox: 2.25 },
  "Teuva": { evLut: 1.75, orthodox: 2.0 },
  "Tohmajärvi": { evLut: 1.8, orthodox: 2.0 },
  "Toholampi": { evLut: 2.0, orthodox: 2.0 },
  "Toivakka": { evLut: 1.45, orthodox: 1.95 },
  "Tornio": { evLut: 1.45, orthodox: 2.25 },
  "Turku": { evLut: 1.2, orthodox: 1.75 },
  "Tuusniemi": { evLut: 1.45, orthodox: 2.0 },
  "Tuusula": { evLut: 1.45, orthodox: 1.8 },
  "Tyrnävä": { evLut: 1.8, orthodox: 2.25 },
  "Ulvila": { evLut: 1.65, orthodox: 2.0 },
  "Urjala": { evLut: 1.7, orthodox: 1.8 },
  "Utajärvi": { evLut: 1.65, orthodox: 2.25 },
  "Utsjoki": { evLut: 1.7, orthodox: 2.25 },
  "Uurainen": { evLut: 1.95, orthodox: 1.95 },
  "Uusikaarlepyy": { evLut: 1.9, orthodox: 2.0 },
  "Uusikaupunki": { evLut: 1.5, orthodox: 1.75 },
  "Vaala": { evLut: 1.65, orthodox: 2.25 },
  "Vaasa": { evLut: 1.4, orthodox: 2.0 },
  "Valkeakoski": { evLut: 1.6, orthodox: 2.0 },
  "Vantaa": { evLut: 1.0, orthodox: 1.8 },
  "Varkaus": { evLut: 1.45, orthodox: 1.95 },
  "Vehmaa": { evLut: 1.85, orthodox: 1.75 },
  "Vesanto": { evLut: 1.85, orthodox: 2.0 },
  "Vesilahti": { evLut: 1.65, orthodox: 2.0 },
  "Veteli": { evLut: 1.85, orthodox: 2.0 },
  "Vieremä": { evLut: 1.8, orthodox: 2.0 },
  "Vihti": { evLut: 1.45, orthodox: 1.8 },
  "Viitasaari": { evLut: 1.8, orthodox: 1.95 },
  "Vimpeli": { evLut: 1.7, orthodox: 2.0 },
  "Virolahti": { evLut: 1.55, orthodox: 1.95 },
  "Virrat": { evLut: 1.75, orthodox: 2.0 },
  "Vårdö": { evLut: 1.95, orthodox: 1.75 },
  "Vöyri": { evLut: 1.95, orthodox: 2.0 },
  "Ylitornio": { evLut: 1.7, orthodox: 2.25 },
  "Ylivieska": { evLut: 1.75, orthodox: 2.0 },
  "Ylöjärvi": { evLut: 1.65, orthodox: 2.0 },
  "Ypäjä": { evLut: 1.8, orthodox: 1.8 },
  "Ähtäri": { evLut: 1.7, orthodox: 2.0 },
  "Äänekoski": { evLut: 1.6, orthodox: 1.95 },
};

/** The church tax rate to apply for a member: the municipality's Evangelical Lutheran parish
 *  rate when the municipality is known, else the national average. */
export function churchTaxRateFor(municipality: string | undefined, denomination: 'evLut' | 'orthodox' = 'evLut'): number {
  const row = municipality ? FINNISH_PARISH_TAX_RATES_2026[municipality] : undefined;
  return row ? row[denomination] : PARISH_TAX_AVG_RATE_PCT;
}
